import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MatSidenav } from '@angular/material/sidenav';

import { BodyClassRenderer } from '@firestitch/body';
import { Item, ItemActionType, SidenavConfig } from '@firestitch/sidenav';
import { FsStore } from '@firestitch/store';

import { Subject, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil } from 'rxjs/operators';

import { Account, AclPermission, Environment } from '@app/common';
import { AclService, SignoutService } from '@app/core';

import { SidenavService } from '../../services/sidenav.service';
import { EnvironmentMenuComponent } from '../environment-menu/environment-menu.component';


@Component({
  selector: 'app-page-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent implements OnInit, OnDestroy {

  @ViewChild('sidenav', { static: true })
  public sidenav: MatSidenav;

  public disableClose: boolean;
  public sideNavMain: SidenavConfig = { items: [] };
  public sideNavBottom: SidenavConfig = { items: [] };
  public workspaceSidenavItems = [];
  public unreadNotifications: number;
  public account: Account;

  private _destroy$ = new Subject();

  constructor(
    public sidenavService: SidenavService,
    private _store: FsStore,
    private _router: Router,
    private _aclQueryService: AclService,
    private _bodyClassRenderer: BodyClassRenderer,
    private _signoutService: SignoutService,
    private _cdRef: ChangeDetectorRef,
    private _aclService: AclService,
  ) {
    this._bodyClassRenderer.addBodyClass('body-side-nav');
    this.disableClose = this.sidenavService.mode !== 'over';

    this.sidenavService.resize$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.disableClose = this.sidenavService.mode !== 'over';
        if (this.sidenavService.opened) {
          this.sidenav.open();
        }
        this._cdRef.markForCheck();
      });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => window.location.pathname),
        distinctUntilChanged(),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadMainSidenav();
      });
  }

  public get environment(): Environment {
    return this._store.get('environment');
  }

  public ngOnInit(): void {
    this.account = this._store.get('account');
    this._buildSidenavBottom();

    this._store.observe('permissions')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._buildSidenavBottom();
      });

    this._store.observe('environment')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._buildSidenavBottom();
      });

    this._store.observe('account')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe((store) => {
        this.account = store.value;
        this._buildSidenavBottom();
      });
  }

  public clickNav($event: any): void {
    this.sidenavService.hide();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _loadMainSidenav(): void {
    this.sideNavMain.items = [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'dashboard',
      },
    ];

    if (this._aclService.hasWrite(AclPermission.Staff, this.environment?.id)) {
      this.sideNavMain.items.push({
        label: 'Staff',
        path: '/staff',
        icon: 'people',
      });
    }

    this._cdRef.markForCheck();
  }

  private _buildSidenavBottom(): void {
    iif(
      () => !!(this.account && this.account.id),
      of([]),
    )
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {

        let items: Item[] = [];

        const organizationItem = this._getOrganizationItem();
        if (organizationItem) {
          items.push(organizationItem);
        }

        if (this._aclQueryService.isAdmin()) {
          items = items.concat([
            {
              label: 'Admin',
              path: '/admin',
              icon: 'settings',
              class: 'admin',
              items: [
                {
                  label: 'Dashboard',
                  path: '/admin/dashboard',
                  exact: false,
                },
                {
                  label: 'Accounts',
                  path: '/admin/accounts',
                  exact: false,
                },
                {
                  label: 'Funds',
                  path: '/admin/funds',
                  exact: false,
                },
                {
                  label: 'Messages',
                  path: '/admin/message',
                  exact: false,
                },
                {
                  label: 'Content',
                  path: '/admin/content',
                  exact: false,
                },
                {
                  label: 'Organizations',
                  path: '/admin/organizations',
                  exact: false,
                },
                {
                  label: 'Reports',
                  path: '/admin/reports',
                  exact: false,
                },
              ],
            },
          ]);
        }

        if (this._aclQueryService.hasPermissionSystem()) {
          items = items.concat([
            {
              label: 'System',
              path: '/system',
              icon: 'personal_video',
              items: [
                {
                  label: 'Settings',
                  path: '/system/settings',
                },
                {
                  label: 'Crons',
                  path: '/system/crons',
                },
                {
                  label: 'File Manager',
                  path: '/system/files',
                },
                {
                  label: 'Processes',
                  path: '/system/processes',
                },
                {
                  label: 'Logs',
                  path: '/system/logs',
                },
                {
                  label: 'Info',
                  path: '/system/info',
                },
              ],
            },
          ]);
        }

        items.push({
          label: 'Terms & Privacy',
          path: '/terms-privacy',
          icon: 'shield',
        });

        if (this.account) {
          items.push({
            label: `${this.account.name}`,
            superlabel: 'My Account',
            path: '/account',
            image: this.account.image.small,
          });
        }

        items.push({
          label: 'Sign Out',
          icon: 'exit_to_app',
          click: () => {
            this._signoutService.signout();
          },
        });

        this.sideNavBottom = { items };
        this._cdRef.markForCheck();
      });
  }

  private _getOrganizationItem() {
    let organizationItem: any = {
      label: 'No Organization',
      exact: true,
      actions: [],
    };

    organizationItem.actions
      .push({
        type: ItemActionType.Menu,
        icon: 'more_vert',
        menu: {
          component: EnvironmentMenuComponent,
        },
      });

    const environment = this.environment;

    if (environment) {
      organizationItem = {
        ...organizationItem,
        superlabel: 'Organization',
        icon: 'account_balance',
      };

      if (this._aclService.hasPermissionAdmin()) {
        organizationItem = {
          ...organizationItem,
          path: `/organization/${environment.id}/settings`,
        };
      }

      organizationItem.label = environment.name;
    }

    return organizationItem;
  }
}
